// THE BODY STANDS UNDER THE HEAD, across tracking gaps too (vr-redon,
// 2026-11-02; APP_PLAYER "VR walking"). Pure state: no THREE, no DOM, so
// scripts/check-feel.mjs row 12 drives it with the real Player.
//
// HeadSeat.fold is main.js headFold's rule (vr-sink, 2026-11-01): `ref` = the
// head's floor point in rig (reference) space the rig placement compensates
// (rig = body − R(yaw)·ref, so the eye stands over the body), `move` = this
// tick's head displacement in world m, the body's move (player.step
// extras.headMove). Zero while a VR flight owns the rig and at ⟲ Recenter.
//
// A TRACKING DISCONTINUITY (the headset taken off and put back on: the runtime
// re-establishes tracking, re-estimates the floor, may move the origin) left
// that baseline stale — the gap was folded into the body as one huge step and
// the eye stood at the new floor's head height ("half sunken and can't move",
// session stuck-061052). Now a discontinuity RE-SEATS instead of folding:
//   - its signals: the reference space's 'reset', the session's visibility
//     back to 'visible' (from 'hidden' / 'visible-blurred') — main.js calls
//     invalidate() — and a single-frame head jump over SEAT_JUMP_M (the floor
//     point or the height; no head moves 0.5 m in one frame);
//   - the re-seat: ref re-based on the current head (no motion integrated
//     across the gap: move = 0, the body holds), the eye height kept (`lift`:
//     the running head height before the gap minus the head now, past
//     SEAT_EYE_DEAD_M, clamped to SEAT_EYE_MAX_M; main.js adds it to the rig),
//     and main.js stands the body on the triangle-exact ground under the head
//     (seatBody: never below it).
// While the viewer pose is emulated (emulatedPosition, or no pose) the head's
// translation is never integrated into the body (orientation only: 'hold');
// the first real frame after it re-seats.

export const SEAT_JUMP_M = 0.5;      // a single-frame head jump over this = a discontinuity
export const SEAT_EYE_TAU_S = 6;     // the standing head height's running mean
export const SEAT_EYE_DEAD_M = 0.1;  // a floor shift under this is noise: no lift change
export const SEAT_EYE_MAX_M = 0.75;  // the lift never exceeds this
export const CTRL_HINT_MS = 1000;    // no controller this long → the "controllers asleep" hint

export class HeadSeat {
  constructor() {
    this.ref = { x: 0, z: 0 };
    this.move = { x: 0, z: 0 };
    this.begin();
  }

  // a new XR session: a new floor, nothing to keep
  begin() {
    this.ref.x = this.ref.z = 0;
    this.move.x = this.move.z = 0;
    this.lift = 0;          // m added to the rig's y (the eye height kept across a floor shift)
    this.lastY = null;      // the last real head height (reference space)
    this.meanY = null;      // its running mean (SEAT_EYE_TAU_S)
    this.live = false;      // ref = the last real head (a jump test is meaningful)
    this.stale = false;     // a discontinuity is pending: the next real frame re-seats
    this.emulated = false;
    this.why = null;
    this.reseats = 0;
  }

  // a VR flight owns the rig / ⟲ Recenter (the origin moves to the head)
  zero() { this.ref.x = this.ref.z = 0; this.live = false; }

  // 'reset' | 'visible' | 'position' | 'jump'
  invalidate(why) { this.stale = true; this.why = why; }

  // hx, hy, hz: the head (the eyes' midpoint) in reference space; yaw: the
  // rig's; emulated: the viewer pose's emulatedPosition (or no pose); flight:
  // a VR flight owns the rig → 'flight' | 'hold' | 'reseat' | 'fold'
  fold(hx, hy, hz, yaw, emulated, flight, dt) {
    const m = this.move, r = this.ref;
    m.x = m.z = 0;
    if (emulated) { this.emulated = true; if (flight) this.zero(); return 'hold'; }
    if (this.emulated) { this.emulated = false; this.invalidate('position'); }
    if (flight) { this.zero(); this._mean(hy, dt); return 'flight'; }
    if (!this.stale && this.live && this.lastY !== null
      && (Math.hypot(hx - r.x, hz - r.z) > SEAT_JUMP_M || Math.abs(hy - this.lastY) > SEAT_JUMP_M)) this.invalidate('jump');
    if (this.stale) {
      if (this.meanY !== null && Math.abs(this.meanY - hy) > SEAT_EYE_DEAD_M) {
        const l = this.meanY + this.lift - hy;
        this.lift = Math.max(-SEAT_EYE_MAX_M, Math.min(SEAT_EYE_MAX_M, l));
      }
      r.x = hx; r.z = hz;
      this.lastY = this.meanY = hy;
      this.live = true; this.stale = false; this.reseats++;
      return 'reseat';
    }
    const dx = hx - r.x, dz = hz - r.z, c = Math.cos(yaw), s = Math.sin(yaw);
    m.x = dx * c + dz * s;
    m.z = -dx * s + dz * c;
    r.x = hx; r.z = hz;
    this._mean(hy, dt);
    this.live = true;
    return 'fold';
  }

  _mean(hy, dt) {
    this.lastY = hy;
    this.meanY = this.meanY === null ? hy : this.meanY + (hy - this.meanY) * Math.min(1, Math.max(0, dt) / SEAT_EYE_TAU_S);
  }
}

// a re-seat stands the body ON the triangle-exact ground under the head
// (Player._groundY → ground.js; the head is over the body once ref is
// re-based), never below it, with no velocity carried across the gap. A
// jet-suit flight, a climb, a carry or a swim keeps its own seat. → the ground y | null
export function seatBody(p) {
  if (!p || p.gliding || p.state === 'climb' || p.carried || p.swimming) return null;
  const g = p._groundY(p.pos.x, p.pos.y, p.pos.z);
  if (g === null) return null;
  if (p.grounded || p.pos.y < g) {
    p.pos.y = g;
    p.vel.set(0, 0, 0);
    p.grounded = true;
    if (p.state === 'air') p.state = p.bike ? 'bike' : 'ground';
  }
  return g;
}

// the controllers asleep (Quest: hand tracking takes over 4 s after the
// controllers rest; a tracked hand has no locomotion): `n` = this poll's
// controller sources (VRControls.poll out.ctrl). Shown after CTRL_HINT_MS
// without one, hidden at once when one is back → whether the hint shows
export class CtrlWatch {
  constructor(ms = CTRL_HINT_MS) { this.ms = ms; this.reset(); }
  reset() { this.since = null; this.show = false; }
  update(n, now) {
    if (n > 0) { this.since = null; this.show = false; return false; }
    if (this.since === null) this.since = now;
    this.show = now - this.since >= this.ms;
    return this.show;
  }
}
